import { CaptionBold } from '@comeon/ui';
import { FC } from 'react';
import styled from 'styled-components';
import useQueryExploreQuickLinks from '~/components/sportsbook/lobby/queries/quick-links/use-query-quick-links';
import SportsbookSportIcon from '~/components/sportsbook/lobby/components/sportsbook-sport-icon';
import { Link } from '~/utils/navigation';
import Icon from '~/components/icon';
import {
	trackSportsbookOpenQuickLinkCustom,
	trackSportsbookOpenQuickLinkLeague,
	trackSportsbookOpenQuickLinkSport
} from '@comeon/analytics';
import { QuickLinkTypeDto } from '~/components/sportsbook/lobby/api/sbapi/sbapi-api-model';
import SportsbookHorizontalScrollArea from '../../../components/sportsbook-horizontal-scroll-area';

// Remember, this component is rendered only for mobile.
const StyledScrollAreaView = styled(SportsbookHorizontalScrollArea.View)`
	min-height: 48px;
	gap: 4px;
	padding: 0.5px 0;
`;

const StyledLink = styled(Link)`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	min-width: 65px;
	max-width: 65px;

	&:first-child {
		margin-left: 12px;
	}
	&:last-child {
		margin-right: 12px;
	}
`;

const StyledIcon = styled(Icon)`
	flex-shrink: 0;
	fill: var(--main-action-icons);
`;

const StyledLabel = styled(CaptionBold)`
	max-width: 100%;

	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;

	color: var(--main-action-icons);
`;

const HorizontalScrollableArea = ({ children }: { children?: React.ReactNode }) => {
	return (
		<SportsbookHorizontalScrollArea.Root>
			<SportsbookHorizontalScrollArea.LeftButton />
			<StyledScrollAreaView>{children}</StyledScrollAreaView>
			<SportsbookHorizontalScrollArea.RightButton />
		</SportsbookHorizontalScrollArea.Root>
	);
};

const SportsbookExploreQuickLinks: FC = () => {
	const { data: quickLinks, isError, isPending } = useQueryExploreQuickLinks();

	if (isPending) {
		return <HorizontalScrollableArea />;
	}

	// No Quick links
	if (isError || quickLinks.length === 0) {
		return null;
	}

	return (
		<HorizontalScrollableArea>
			{quickLinks.map((quickLink, i) => (
				<StyledLink
					onClick={() => {
						switch (quickLink.type) {
							case QuickLinkTypeDto.SPORT:
								trackSportsbookOpenQuickLinkSport(
									i + 1,
									quickLink.labelName,
									quickLink.id,
									quickLink.sportId,
									quickLink.sportName
								);
								break;
							case QuickLinkTypeDto.LEAGUE:
								trackSportsbookOpenQuickLinkLeague(
									i + 1,
									quickLink.labelName,
									quickLink.id,
									quickLink.leagueId,
									quickLink.leagueName
								);
								break;
							case QuickLinkTypeDto.CUSTOM:
								trackSportsbookOpenQuickLinkCustom(
									i + 1,
									quickLink.labelName,
									quickLink.id,
									quickLink.url
								);
								break;
							default:
						}
					}}
					href={quickLink.url}
					key={`explore-sport-link-${quickLink.id}`}
				>
					<StyledIcon as={SportsbookSportIcon} type="" icon={quickLink.icon} width="24" height="24" />
					<StyledLabel>{quickLink.labelName}</StyledLabel>
				</StyledLink>
			))}
		</HorizontalScrollableArea>
	);
};

export default SportsbookExploreQuickLinks;
